import { CSSObject, keyframes } from '@emotion/css'
import styled from '@emotion/styled'
import { withClass } from '@news-mono/web-common'
import { IconLightning } from '../../icons/IconLightning/IconLightning'
import { PerthNowIcon } from '../../logos/PerthNowIcon/PerthNowIcon'
import { TheWestMark } from '../../logos/TheWestMark/TheWestMark'
import { colors } from '../../__styling/settings/colors'
import { perthNowSectionDefinitions } from '../../__styling/settings/sections/perthnow'
import { calcRem } from '../../__styling/style-functions/calc-rem'
import { themedValue } from '../../__styling/themed-value'

interface StyledEditorialLabelProps {
    isBreaking: boolean
    cardHasBackground?: boolean
    isLarge?: boolean
}

export const StyledEditorialLabelClassName = 'StyledEditorialLabelClass'

export const StyledEditorialLabel = withClass(StyledEditorialLabelClassName)(
    styled('div')<StyledEditorialLabelProps>(
        {
            textTransform: 'uppercase',
        },
        ({ theme, isBreaking, cardHasBackground, isLarge }) => [
            {
                fontWeight: themedValue(theme, {
                    thewest: 700,
                    perthnow: 700,
                    sevennews: 400,
                }),
                fontSize: themedValue(theme, {
                    thewest: isLarge ? calcRem(15) : calcRem(11),
                    perthnow: isLarge ? calcRem(20) : calcRem(11),
                    sevennews: isLarge ? calcRem(18) : calcRem(16),
                    fallback: undefined,
                }),
                fontFamily: themedValue(theme, {
                    thewest: theme.fonts.sansSerif,
                    perthnow: theme.fonts.sansSerifCond,
                    sevennews: theme.fonts.headingAlternative,
                }),
                color: themedValue(theme, {
                    thewest: isBreaking
                        ? colors.thewest.ladybird
                        : (section) => section.primaryColor,
                    sevennews: colors.white,
                    perthnow: cardHasBackground
                        ? colors.white
                        : theme.colors.text.primary,
                    fallback: cardHasBackground ? colors.white : undefined,
                }),
                letterSpacing: themedValue(theme, {
                    sevennews: 'normal',
                    thewest: 'normal',
                    fallback: '0.05em',
                }),
            },
        ],
    ),
)

export const StyledEditorialLabelText = styled('span')({
    marginLeft: calcRem(0),
})

interface Keyframes {
    [key: string]: CSSObject
}

const liveBlinkAnimation: Keyframes = {
    from: {
        opacity: 1,
    },
    to: {
        opacity: 0.1,
    },
}

export const StyledLiveBubble = styled('div')(
    {
        display: 'inline-block',
        borderRadius: '100%',
        width: '0.7em',
        height: '0.7em',
        animation: `${keyframes(liveBlinkAnimation)} 1s infinite ease`,

        '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
        },
    },
    ({ theme }) => [
        {
            backgroundColor: themedValue(theme, {
                thewest: theme.colors.brand,
                perthnow: perthNowSectionDefinitions.default.primaryColor,
                fallback: 'currentColor',
            }),
        },
    ],
)

interface StyledPerthNowBlockProps {
    isLarge?: boolean
}

export const StyledPerthNowBlock = styled('div')<StyledPerthNowBlockProps>(
    {
        display: 'inline-block',
        backgroundColor: perthNowSectionDefinitions.default.primaryColor,
    },
    ({ isLarge }) => [
        {
            width: isLarge ? calcRem(15) : calcRem(8),
            height: isLarge ? calcRem(15) : calcRem(8),
            marginTop: isLarge ? calcRem(2) : calcRem(1),
        },
    ],
)

interface StyledIconProps {
    isLarge?: boolean
}

export const StyledTheWestMark = styled(TheWestMark)<StyledIconProps>(
    {
        width: calcRem(12),
        height: calcRem(10),
    },
    ({ isLarge }) => [
        isLarge && {
            width: calcRem(15),
            height: calcRem(13),
        },
    ],
)

export const StyledPerthNowIcon = styled(PerthNowIcon)<StyledIconProps>(
    {
        width: calcRem(9),
        height: calcRem(9),
        transform: `translateY(1px)`,
    },
    ({ isLarge }) => [
        isLarge && {
            width: calcRem(16),
            height: calcRem(16),
            transform: `translateY(2px)`,
        },
    ],
)

export const StyledIconLightning = styled(IconLightning)<StyledIconProps>(
    {
        width: calcRem(7),
        height: calcRem(10),
        transform: `translateY(1px)`,
    },
    ({ theme, isLarge }) => [
        isLarge &&
            themedValue(theme, {
                perthnow: {
                    width: calcRem(12),
                    height: calcRem(16),
                    transform: `translateY(2px)`,
                },
                thewest: {
                    width: calcRem(10),
                    height: calcRem(14),
                    transform: 'translateY(2px)',
                },
                fallback: undefined,
            }),
    ],
)
